import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

function RestaurantManagement() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    fetchRestaurants();
  }, [statusFilter]);

  const fetchRestaurants = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (statusFilter === 'approved') params.isApproved = 'true';
      if (statusFilter === 'pending') params.approvalStatus = 'pending';
      if (statusFilter === 'rejected') params.approvalStatus = 'rejected';

      const response = await adminAPI.getAllRestaurants(params);
      setRestaurants(response.data.data.restaurants || []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setError('Failed to load restaurants');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (restaurant) => {
    try {
      setError('');
      await adminAPI.toggleRestaurantVisibility(restaurant._id);
      setSuccess(`"${restaurant.name}" is now ${restaurant.isVisible === false ? 'visible' : 'hidden'} in user app`);
      fetchRestaurants();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to toggle visibility');
    }
  };

  const handleToggleMultiCoupon = async (restaurant) => {
    try {
      setError('');
      await adminAPI.toggleRestaurantMultiCoupon(restaurant._id);
      setSuccess(`"${restaurant.name}" multi-coupon is now ${restaurant.allowMultipleCoupons ? 'disabled' : 'ENABLED'}`);
      fetchRestaurants();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to toggle multi-coupon');
    }
  };

  const handleDeleteClick = (restaurant) => {
    setRestaurantToDelete(restaurant);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== restaurantToDelete.name) {
      setError('Restaurant name does not match. Please type the exact name to confirm deletion.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminAPI.deleteRestaurant(restaurantToDelete._id);
      setSuccess(`Successfully deleted "${restaurantToDelete.name}" (${response.data.data.deletedMenuItems} menu items, ${response.data.data.deletedCoupons} coupons removed)`);
      setShowDeleteModal(false);
      setRestaurantToDelete(null);
      setDeleteConfirmText('');
      fetchRestaurants();
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      setError(error.response?.data?.message || 'Failed to delete restaurant');
    } finally {
      setLoading(false);
    }
  };

  const filteredRestaurants = restaurants.filter(restaurant =>
    restaurant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    restaurant.owner?.phone?.includes(searchTerm) ||
    restaurant.address?.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>Restaurant Management</h1>
        <p style={{ color: '#666' }}>View, manage, and delete restaurants from the platform</p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fee2e2',
          color: '#dc2626',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>
      )}
      {success && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#dcfce7',
          color: '#16a34a',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{success}</span>
          <button onClick={() => setSuccess('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>
      )}

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="Search by name, phone, or city..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '10px 16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            width: '300px',
            fontSize: '14px'
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: '10px 16px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: 'white'
          }}
        >
          <option value="all">All Restaurants</option>
          <option value="approved">Approved Only</option>
          <option value="pending">Pending Approval</option>
          <option value="rejected">Rejected</option>
        </select>
        <button
          onClick={fetchRestaurants}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        <span style={{ color: '#666', marginLeft: 'auto' }}>
          {filteredRestaurants.length} restaurant(s)
        </span>
      </div>

      {/* Restaurant Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Restaurant</th>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Owner</th>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>City</th>
              <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Status</th>
              <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Visibility</th>
              <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Created</th>
              <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && restaurants.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  Loading restaurants...
                </td>
              </tr>
            ) : filteredRestaurants.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  No restaurants found
                </td>
              </tr>
            ) : (
              filteredRestaurants.map((restaurant) => (
                <tr key={restaurant._id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {restaurant.photos?.[0] ? (
                        <img
                          src={restaurant.photos[0]}
                          alt={restaurant.name}
                          style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          backgroundColor: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px'
                        }}>
                          🍽️
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: '500' }}>{restaurant.name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{restaurant._id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: '14px' }}>{restaurant.owner?.name || 'N/A'}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{restaurant.owner?.phone || 'N/A'}</div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '14px' }}>
                    {restaurant.address?.city || 'N/A'}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: restaurant.isApproved ? '#dcfce7' :
                                       restaurant.approvalStatus === 'rejected' ? '#fee2e2' : '#fef3c7',
                      color: restaurant.isApproved ? '#16a34a' :
                             restaurant.approvalStatus === 'rejected' ? '#dc2626' : '#ca8a04'
                    }}>
                      {restaurant.isApproved ? 'Approved' :
                       restaurant.approvalStatus === 'rejected' ? 'Rejected' : 'Pending'}
                    </span>
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleToggleVisibility(restaurant)}
                      style={{
                        padding: '5px 14px',
                        backgroundColor: restaurant.isVisible === false ? '#fef2f2' : '#f0fdf4',
                        color: restaurant.isVisible === false ? '#dc2626' : '#16a34a',
                        border: `1px solid ${restaurant.isVisible === false ? '#fecaca' : '#bbf7d0'}`,
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      {restaurant.isVisible === false ? 'Hidden' : 'Visible'}
                    </button>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '14px', color: '#666' }}>
                    {formatDate(restaurant.createdAt)}
                  </td>
                  <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleToggleMultiCoupon(restaurant)}
                      title="Allow more than one coupon at once. When ON, the backend auto-stacks the best complementary auto-apply coupon on top of the customer's chosen one. No app update needed."
                      style={{
                        padding: '5px 12px',
                        marginRight: '8px',
                        backgroundColor: restaurant.allowMultipleCoupons ? '#f0fdf4' : '#f8fafc',
                        color: restaurant.allowMultipleCoupons ? '#16a34a' : '#64748b',
                        border: `1px solid ${restaurant.allowMultipleCoupons ? '#bbf7d0' : '#e2e8f0'}`,
                        borderRadius: '20px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}
                    >
                      {restaurant.allowMultipleCoupons ? '🎟️ Multi-coupon: ON' : 'Multi-coupon: OFF'}
                    </button>
                    <button
                      onClick={() => handleDeleteClick(restaurant)}
                      style={{
                        padding: '6px 16px',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && restaurantToDelete && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px'
              }}>
                ⚠️
              </div>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>Delete Restaurant</h3>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>This action cannot be undone</p>
              </div>
            </div>

            <div style={{
              backgroundColor: '#fef2f2',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '500' }}>You are about to delete:</p>
              <p style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600' }}>{restaurantToDelete.name}</p>
              <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                Owner: {restaurantToDelete.owner?.name || 'N/A'} | {restaurantToDelete.owner?.phone || 'N/A'}
              </p>
            </div>

            <p style={{ fontSize: '14px', marginBottom: '8px' }}>
              This will permanently delete:
            </p>
            <ul style={{ fontSize: '14px', color: '#666', marginBottom: '16px', paddingLeft: '20px' }}>
              <li>The restaurant profile</li>
              <li>All menu items</li>
              <li>All coupons</li>
              <li>Elasticsearch index entry</li>
            </ul>

            <p style={{ fontSize: '14px', marginBottom: '8px' }}>
              Type <strong>{restaurantToDelete.name}</strong> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type restaurant name here..."
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                marginBottom: '16px',
                boxSizing: 'border-box'
              }}
            />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setRestaurantToDelete(null);
                  setDeleteConfirmText('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={loading || deleteConfirmText !== restaurantToDelete.name}
                style={{
                  padding: '10px 20px',
                  backgroundColor: deleteConfirmText === restaurantToDelete.name ? '#dc2626' : '#fca5a5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: deleteConfirmText === restaurantToDelete.name ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Deleting...' : 'Delete Restaurant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RestaurantManagement;
